import { createTheme, type Theme, ThemeProvider } from '@mui/material';
import { deDE, enUS, frFR } from '@mui/material/locale';
import { enUS as chartsEnUS, frFR as chartsFrFR } from '@mui/x-charts-pro/locales';
import { deDE as gridDeDE, enUS as gridEnUS, frFR as gridFrFR } from '@mui/x-data-grid-pro/locales';
import { enUS as pickersEnUS, frFR as pickersFrFR } from '@mui/x-date-pickers-pro/locales';
import { type PropsWithChildren, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { type LangCode } from '@@lib/i18n/constants';
import { useSettingsStore } from '@@scripts/store/settings/useSettingsStore';

const MUI_LOCALES: Record<LangCode, object> = {
    'de-CH': deDE,
    'fr-CH': frFR,
    'en-GB': enUS,
};

const MUI_CHARTS_LOCALES: Record<LangCode, object> = {
    // German translations for charts do not exist yet
    'de-CH': {
        ...chartsEnUS,
        components: {
            ...chartsEnUS.components,
            MuiChartsLocalizationProvider: {
                ...chartsEnUS.components.MuiChartsLocalizationProvider,
                defaultProps: {
                    ...chartsEnUS.components.MuiChartsLocalizationProvider.defaultProps,
                    localeText: {
                        ...chartsEnUS.components.MuiChartsLocalizationProvider.defaultProps,
                        loading: 'Lade Daten...',
                        noData: 'Keine Daten verfügbar',
                    },
                },
            },
        },
    },
    'fr-CH': chartsFrFR,
    'en-GB': chartsEnUS,
};

const MUI_PICKERS_LOCALES: Record<LangCode, object> = {
    // German translations for pickers do not exist yet
    'de-CH': {
        ...pickersEnUS,
        components: {
            ...pickersEnUS.components,
            MuiLocalizationProvider: {
                ...pickersEnUS.components.MuiLocalizationProvider,
                defaultProps: {
                    ...pickersEnUS.components.MuiLocalizationProvider.defaultProps,
                    localeText: {
                        ...pickersEnUS.components.MuiLocalizationProvider.defaultProps.localeText,
                        start: 'Von',
                        end: 'Bis',
                    },
                },
            },
        },
    },
    'fr-CH': pickersFrFR,
    'en-GB': pickersEnUS,
};

const MUI_DATA_GRID_LOCALES: Record<LangCode, object> = {
    'de-CH': gridDeDE,
    'fr-CH': gridFrFR,
    'en-GB': gridEnUS,
};

const LocalizedThemeProvider = ({ children, theme }: PropsWithChildren<{ theme: Theme }>) => {
    const { t } = useTranslation();
    const uiLocale = useSettingsStore((state) => state.uiLocale);
    // A new theme re-renders every styled component, so it changes only with the UI language
    const localizedTheme = useMemo(
        () =>
            createTheme(
                theme,
                MUI_LOCALES[uiLocale],
                MUI_CHARTS_LOCALES[uiLocale],
                MUI_PICKERS_LOCALES[uiLocale],
                MUI_DATA_GRID_LOCALES[uiLocale],
                {
                    components: {
                        MuiDataGrid: {
                            defaultProps: {
                                localeText: { noRowsLabel: t('list.noresult') },
                            },
                        },
                    },
                },
            ),
        [theme, uiLocale, t],
    );

    return <ThemeProvider theme={localizedTheme}>{children}</ThemeProvider>;
};

export default LocalizedThemeProvider;
